<template>
  <div 
    class="self-start group bg-white/5 rounded-lg overflow-hidden transition-all duration-200 hover:border-teal-500/30 h-max-full min-h-[326px]">
    <div class="relative flex flex-col h-full">
      <!-- Flow Preview Image -->
      <div @click="$router.push({ name: 'flows.show', params: { flowId: flow.id } })" class="h-40 flex items-center justify-center bg-gradient-to-br from-teal-900/20 to-transparent cursor-pointer">
        <GitBranch class="h-16 w-16 text-teal-400/30" />
      </div>
      
      <!-- Status Badge -->
      <div class="absolute top-3 right-3">
        <StatusBadge :status="flow.status" />
      </div>
    </div>
    
    <!-- Flow Info -->
    <div class="p-4">
      <div class="flex items-start justify-between mb-2">
        <h3 class="font-semibold text-white text-lg truncate">{{ flow.name }}</h3>
      </div>
      
      <p v-if="flow.description" class="text-gray-400 text-sm line-clamp-2 mb-3 h-10">{{ flow.description }}</p>
    </div>
    
    <!-- Action Footer -->
    <div class="p-4 pt-0 flex items-center justify-between items-self-end h-full">
      <div class="flex items-center gap-2">
        <!-- Botão de Fluxo -->
        <TooltipProvider>
          <Tooltip :delay-duration="100">
            <TooltipTrigger as-child>
              <RouterLink :to="{ name: 'flows.show', params: { flowId: flow.id } }" size="sm" class="text-teal-400 hover:text-teal-300 hover:bg-teal-900/20 flex items-center gap-1 bg-white/5 p-2 rounded-sm">
                <Workflow class="h-3.5 w-3.5" />
              </RouterLink>
            </TooltipTrigger>
            <TooltipContent side="top" :side-offset="5">
              Fluxo de mensagens
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <!-- Botão de Métricas (atualizado) -->
        <TooltipProvider>
          <Tooltip :delay-duration="100">
            <TooltipTrigger as-child>
              <button 
                size="sm" 
                class="text-teal-400 hover:text-teal-300 hover:bg-teal-900/20 flex items-center gap-1 bg-white/5 p-2 rounded-sm"
                @click="loadSummary"
                :disabled="loadingSummary"
              >
                <template v-if="loadingSummary">
                  <Loader2 class="h-3.5 w-3.5 animate-spin" />
                </template>
                <template v-else>
                  <BarChart class="h-3.5 w-3.5" />
                </template>
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" :side-offset="5">
              {{ showMetrics ? 'Ocultar métricas' : 'Ver métricas' }}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <TooltipProvider>
          <Tooltip :delay-duration="100">
            <TooltipTrigger as-child>
              <button class="text-teal-400 hover:text-teal-300 hover:bg-teal-900/20 flex items-center gap-1 bg-white/5 p-2 rounded-sm" @click="$emit('editFlow', flow)">
                <Pencil class="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" :side-offset="5">
              Editar
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider>
          <Tooltip :delay-duration="100">
            <TooltipTrigger as-child>
              <button class="text-teal-400 hover:text-teal-300 hover:bg-teal-900/20 flex items-center gap-1 bg-white/5 p-2 rounded-sm" @click="$emit('deleteFlow', flow)">
                <Trash class="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" :side-offset="5">
              Excluir
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div class="flex items-center gap-1">
        <Switch :checked="flow.status === FlowStatusEnum.ACTIVE" @update:checked="$emit('switchFlow', flow)" />
      </div>
    </div>

    <!-- Seção de Métricas (atualizada) -->
    <div v-if="showMetrics" class="p-4 border-t border-white/10">
      <div v-if="loadingSummary" class="flex justify-center py-4">
        <Loader2 class="h-6 w-6 animate-spin text-teal-400" />
      </div>
      <div v-else-if="summary">
        <!-- Conversion Rate Bar -->
        <ConversionBar :conversionRate="Number(conversionRate)" />
    
        <!-- Metrics Grid -->
        <FlowMetrics :metrics="metrics" />
    
        <!-- Funnel Visualization -->
        <!-- <ConversionFunnel :funnel="funnel" /> -->
      </div>
      <div v-else class="text-center text-gray-400 py-4">
        Nenhum dado disponível
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Flow } from '@/interfaces/chat-flow/flow/Flow';
import { BarChart, GitBranch, Loader2, Pencil, ShoppingCart, Trash, TrendingDown, TrendingUp, Users, Workflow, Zap } from 'lucide-vue-next'
import ConversionBar from './ConversionBar.vue';
import FlowMetrics from './FlowMetrics.vue';
import ConversionFunnel from './ConversionFunnel.vue';
import { computed, ref } from 'vue';
import { BanknotesIcon } from '@heroicons/vue/24/outline';
import { Switch } from '@/components/ui/switch';
import StatusBadge from '@/components/ui/badge/StatusBadge.vue';
import { FlowStatusEnum } from '@/enums/chatFlow/flows/FlowStatusEnum'
import { FlowSummary, useFlow } from '@/composables/chatFlow/useFlow';

defineEmits([
  'editFlow',
  'deleteFlow',
  'switchFlow'
])

const props = defineProps<{
  flow: Flow
}>()

const { getSummary } = useFlow()

// Estado para controle do loading e exibição
const loadingSummary = ref(false)
const showMetrics = ref(false)
const summary = ref<FlowSummary | null>(null)

// Carrega o summary quando o botão é clicado
const loadSummary = async () => {
  showMetrics.value = !showMetrics.value
  
  if (showMetrics.value && !summary.value) {
    try {
      loadingSummary.value = true
      summary.value = await getSummary(props.flow.id)
    } catch (error) {
      console.error('Erro ao carregar métricas:', error)
    } finally {
      loadingSummary.value = false
    }
  }
}

// Calcula os dados com base no summary
const stats = computed(() => ({
  commission: summary.value?.totalCommission || 0,
  leads: summary.value?.leadsCount || 0,
  sales: summary.value?.paidOrdersCount || 0,
  upsells: summary.value?.upsellCount || 0,
  downsells: summary.value?.downsellCount || 0,
  hotLeads: Math.floor((summary.value?.leadsCount || 0) * 0.15) // Exemplo: 15% dos leads são quentes
}))

// Calculate conversion rate (sales / leads)
const conversionRate = computed(() => {
  if (stats.value.leads === 0) return '0.0'
  const rate = (stats.value.sales / stats.value.leads) * 100
  return rate.toFixed(1)
})

// Metrics for the cards
const metrics = computed(() => [
  {
    label: 'Total de Ganhos',
    value: stats.value.commission.toLocaleString('pt-BR', { currency: 'BRL', style: 'currency' }),
    icon: BanknotesIcon,
    change: 12.5 // Você pode calcular isso com base em dados históricos
  },
  {
    label: 'Total de Leads',
    value: stats.value.leads,
    icon: Users,
    change: 8.3
  },
  {
    label: 'Vendas',
    value: stats.value.sales,
    icon: ShoppingCart,
    change: 5.7
  },
  {
    label: 'Upsells',
    value: stats.value.upsells,
    icon: TrendingUp,
    change: 15.2
  },
  {
    label: 'Downsells',
    value: stats.value.downsells,
    icon: TrendingDown,
    change: -3.1
  },
  // {
  //   label: 'Leads Quentes',
  //   value: stats.value.hotLeads,
  //   icon: Zap,
  //   change: 10.0
  // }
])

// Funnel data
const funnel = computed(() => [
  {
    label: 'Leads',
    shortLabel: 'Leads',
    value: stats.value.leads,
    percentage: 100,
    color: 'bg-teal-700/50'
  },
  // {
  //   label: 'Leads Quentes',
  //   shortLabel: 'Quentes',
  //   value: stats.value.hotLeads,
  //   percentage: stats.value.leads > 0 ? Math.round((stats.value.hotLeads / stats.value.leads) * 100) : 0,
  //   color: 'bg-teal-600/60'
  // },
  {
    label: 'Vendas',
    shortLabel: 'Vendas',
    value: stats.value.sales,
    percentage: stats.value.leads > 0 ? Math.round((stats.value.sales / stats.value.leads) * 100) : 0,
    color: 'bg-teal-500/70'
  },
  {
    label: 'Upsells',
    shortLabel: 'Upsells',
    value: stats.value.upsells,
    percentage: stats.value.leads > 0 ? Math.round((stats.value.upsells / stats.value.leads) * 100) : 0,
    color: 'bg-teal-400/80'
  },
  {
    label: 'Downsells',
    shortLabel: 'Downsells',
    value: stats.value.downsells,
    percentage: stats.value.leads > 0 ? Math.round((stats.value.downsells / stats.value.leads) * 100) : 0,
    color: 'bg-teal-300/90'
  }
])
</script>